import { Injectable } from '@angular/core';

import { TickerSymbolStatus } from '../../features/ticker-symbol/ticker-symbol.domain';
import {
  AssetGroups,
  FundamentalData,
  FundValues,
  ModQuote,
  MutualFundsQuote,
  OptionValues,
  Quote,
  QuoteValues,
  RecentQuotes,
  RecentQuotesItem,
  RecentQuotesItemResponse, RecentQuotesQuoteResponse,
  RecentQuotesResponse,
  SecuritySymbol,
  SymbolResponse,
  SymbolType
} from '../../domain/public-api';
import { WatchlistUtils } from '../watchlist/watchlist-utils';
import { UtilityService } from '../../utils/utility.service';

/**
 * Convert recent quotes backend JSON-parsed data into frontend types.
 */
@Injectable({
  providedIn: 'root'
})
export class RecentQuotesMapperService {

  constructor() {}

  /**
   * Convert a single recent quote backend object to a Typescript type.
   */
  mapRecentQuotes(recentQuotesResponse: RecentQuotesResponse, hasFundamentals: boolean): RecentQuotes {
    if (recentQuotesResponse === undefined || recentQuotesResponse.symbolGroupDetails === undefined) {
      return {
        assetGroups: undefined, hasFundamentals: false, name: '', numberOfRemaining: 0, timestamp: undefined
      } ;
    }
    const result: RecentQuotes = WatchlistUtils.copy<RecentQuotes>(recentQuotesResponse, ['symbolGroupDetails']);
    result.assetGroups = this.mapAssetGroups(recentQuotesResponse.symbolGroupDetails);
    result.hasFundamentals = hasFundamentals;
    return result;
  }

  private mapAssetGroups(responseItems: Array<RecentQuotesItemResponse>): AssetGroups<RecentQuotesItem> {
    const assetGroups: AssetGroups<RecentQuotesItem> = new AssetGroups<RecentQuotesItem>();
    responseItems.forEach((responseItem: RecentQuotesItemResponse): void => {
      const recentQuotesItem: RecentQuotesItem = this.mapRecentQuotesItem(responseItem);
      if (recentQuotesItem !== undefined) {
        assetGroups.add(recentQuotesItem);
      }
    });
    return assetGroups;
  }

  /**
   * Create a RecentQuotesItem instance.
   */
  private mapRecentQuotesItem(recentQuotesItemResponse: RecentQuotesItemResponse): RecentQuotesItem {
    const quoteResponse: RecentQuotesQuoteResponse = recentQuotesItemResponse.responseQuote;
    const symbolResponse: SymbolResponse = recentQuotesItemResponse.symbolResponse;

    if (quoteResponse === undefined || symbolResponse === undefined) {
      return undefined;
    }

    const symbol: SecuritySymbol = Object.assign({}, quoteResponse.symbol);
    symbol.id = symbolResponse.id;
    symbol.symbol = symbolResponse.symbol;
    symbol.symbolError = symbolResponse.symbolError;
    if (symbol.symbolType === undefined) {
      symbol.symbolType = symbolResponse.symbolType; // this is not really needed, b/c quoteResponse.symbol.type is just fine (see above)
    }

    const quote: Quote = Object.assign({}, quoteResponse);
    quote.symbol = symbol; // TODO: use this symbol object instead of top level (parallel to quote) one.

    // RENAMING - WHY? // TODO: USE ORIGINAL NAMES
    quote.latestPriceChange = quoteResponse.priceChange;
    quote.latestPercentChange = quoteResponse.percentChange;
    quote.description = quoteResponse.desc;
    quote.securityHalted = quoteResponse.securityHalted; // backend domain SymbolGroupDetail.java -> ResponseQuote.java#securityHalted
    quote.quoteError = quoteResponse.quoteError;

    if (quote.symbol.symbolType === SymbolType.MUTUALFUND) {
      quote.loadType = (quote as MutualFundsQuote).mutualFundLoadType;
    }

    const fundamentalData: FundamentalData = (recentQuotesItemResponse.modQuote !== undefined) ?
      this.getFundamentalData(recentQuotesItemResponse.modQuote, quote.symbol) : undefined;

    const tickerSymbolStatus: TickerSymbolStatus = WatchlistUtils.toSymbolStatus(quoteResponse, symbolResponse);

    const result: RecentQuotesItem = {
      symbol,
      quote,
      fundamentalData,
      tickerSymbolStatus
    } as RecentQuotesItem;
    return result;
  }

  private getFundamentalData(modQuote: ModQuote, symbol: SecuritySymbol): FundamentalData {
    const quoteValues: QuoteValues | OptionValues | FundValues = this.getSymbolSpecificValues(modQuote, symbol);
    const fundamentalData: FundamentalData = (quoteValues !== undefined) ? UtilityService.deepExtend({}, quoteValues) : {};

    if (modQuote.companyName !== undefined) {
      fundamentalData.companyName = modQuote.companyName;
    }
    if (modQuote.nextEarningsDate !== undefined) {
      fundamentalData.nextEarningsDate = modQuote.nextEarningsDate;
    }
    return fundamentalData;
  }

  private getSymbolSpecificValues(modQuote: ModQuote, symbol: SecuritySymbol): QuoteValues | OptionValues | FundValues {
    const symbolType = symbol.symbolType;
    if ((symbolType === SymbolType.STOCK || symbolType === SymbolType.INDEX) && modQuote.quoteValues !== undefined) {
      return modQuote.quoteValues;

    } else if (symbolType === SymbolType.OPTION && modQuote.optionValues !== undefined) {
      if (symbol.call) {
        modQuote.optionValues.type = 'CALL';
      } else {
        modQuote.optionValues.type = 'PUT';
      }
      modQuote.optionValues.expiryType = modQuote.optionValues.periodicity !== undefined ?
        modQuote.optionValues.periodicity.toUpperCase() : '-';
      return modQuote.optionValues;

    } else if (symbolType === SymbolType.MUTUALFUND && modQuote.fundValues !== undefined) {
      return modQuote.fundValues;
    }
    return undefined;
  }
}

